import React, { useState, ChangeEvent, useEffect } from 'react';
import { PartyType } from '../../../../../../../types/SMT';
import {
  SearchSelectDetailed,
  useBoolean,
  useDisclosure,
  SearchButton,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useInsuranceProducts } from '../../../../../../settings/src/features/risk-domains/api/getInsuranceProducts';
import { useInsuranceProductsByRiskObject } from '../../../../../../conversations/src/features/report-generation/api/getInsuranceProductsByRiskObject';
import { useInsuranceProductsByParty } from '../../../../../../conversations/src/features/report-generation/api/getInsuranceProductsByParty';
import { InsuranceProduct } from '../../../../../../settings/src/features/risk-domains/types/InsuranceProducts';
import { validate } from 'uuid';
import { Language } from '../../../../../../../types/Common';

export type RiskType = 'PARTY' | 'RISK-OBJECT' | 'MANAGEMENT' | 'STAFF';

interface Props {
  partyType: PartyType;
  riskType?: RiskType;
  riskId?: string;
  isDisabled?: boolean;
  value?: string;
  onChange: (value: string, label?: string, product?: InsuranceProduct) => void;
  riskDomainId?: string;
  selectedProductIds?: string[];
  language?: Language;
}

const InsuranceProductSearch: React.FC<Props> = ({
  partyType,
  riskType,
  riskId,
  isDisabled,
  value,
  onChange,
  riskDomainId: riskDomain,
  selectedProductIds,
  language,
  ...props
}) => {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [searchValue, setSearchValue] = useState<string>('');
  const [showAllProducts, setShowAllProducts] = useBoolean();

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const { data: riskData, isLoading: isLoadingRiskData } =
    useInsuranceProductsByRiskObject({
      riskObjectId: riskType === 'RISK-OBJECT' ? riskId : undefined,
      lang: language,
    });

  const isPartyData =
    riskType === 'PARTY' || riskType === 'MANAGEMENT' || riskType === 'STAFF';

  const { data: partyData, isLoading: isLoadingPartyData } =
    useInsuranceProductsByParty({
      partyId: isPartyData ? riskId : undefined,
      category:
        riskType === 'MANAGEMENT'
          ? 'management'
          : riskType === 'STAFF'
          ? 'staff'
          : undefined,
    });

  const isPrefilledValueNotFound = Boolean(
    value && validate(value) && !riskData?.items.find(({ id }) => id === value),
  );

  useEffect(() => {
    if (!showAllProducts && isPrefilledValueNotFound) {
      setShowAllProducts.on();
    }
  }, [isPrefilledValueNotFound]);

  const { data: allProducts, isLoading: isLoadingAllProducts } =
    useInsuranceProducts(
      {
        entity: partyType,
        riskDomain,
        lang: language,
      },
      !riskId || showAllProducts,
    );
  const getFilteredData = () => {
    if (!riskId || showAllProducts) {
      return allProducts;
    }

    if (isPartyData) {
      return partyData;
    }

    return riskData;
  };

  const filteredData = getFilteredData();

  const isLoading =
    isLoadingAllProducts || isLoadingRiskData || isLoadingPartyData;

  const productOptions = (filteredData?.items || [])
    .filter(
      ({ name, id }) =>
        name?.toLowerCase().includes(searchValue?.toLowerCase()) &&
        !selectedProductIds?.includes(id),
    )
    .map(({ id, name, description }) => ({
      label: name,
      description: description || '',
      value: id,
    }));

  const handleSelectProduct = (selectedProductId: string) => {
    const product = filteredData?.items?.find(
      ({ id }) => id === selectedProductId,
    );
    onChange(selectedProductId, product?.name, product);
    onClose();
  };

  const selectedProductLabel = productOptions.find(
    ({ value: optionValue }) => optionValue === value,
  )?.label;

  return (
    <>
      <SearchSelectDetailed
        isOpen={isOpen}
        onClose={onClose}
        options={productOptions}
        isLoading={isLoading}
        searchValue={searchValue}
        onChange={handleSearchChange}
        onSelect={handleSelectProduct}
        extraActionLabel={
          riskId && !showAllProducts
            ? t('INSURANCE-PRODUCT-SEARCH_EMPTY-RISK-DATA-ACTION')
            : showAllProducts
            ? t('INSURANCE-PRODUCT-SEARCH_EMPTY-RISK-DATA-ACTION_DISABLE')
            : undefined
        }
        emptyText={
          riskId && filteredData?.items.length === 0
            ? t('INSURANCE-PRODUCT-SEARCH_EMPTY-RISK-DATA')
            : undefined
        }
        extraActionOnClick={setShowAllProducts.toggle}
      />
      <SearchButton
        onClick={onOpen}
        placeholder={t('CHOOSE-A-PRODUCT')}
        isDisabled={isDisabled}
        value={selectedProductLabel}
        {...props}
      />
    </>
  );
};

export default InsuranceProductSearch;
